import { useCallback, useEffect, useRef, useState } from 'react';
import type { EditorRef } from 'react-email-editor';
import useLatestRef from '../shared/useLatestRef';
import { extractOfferIndices } from '../shared/offerTokens';
import { InsertedBlock, UnlayerDesign, UnlayerMergeTags, appendBlock } from './unlayerDesign';

export interface UnlayerContent {
  html: string;
  design: object;
}

interface UseUnlayerEditorOptions {
  initialDesign?: object;
  initialHtml?: string;
  mergeTags: UnlayerMergeTags;
  onOfferIndicesChange?: (indices: Set<number>) => void;
}

const useUnlayerEditor = ({ initialDesign, initialHtml, mergeTags, onOfferIndicesChange }: UseUnlayerEditorOptions) => {
  const editorRef = useRef<EditorRef>(null);
  const initialRef = useRef({ design: initialDesign, html: initialHtml });
  const latestContentRef = useRef<UnlayerContent | null>(null);
  const onOfferIndicesChangeRef = useLatestRef(onOfferIndicesChange);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (ready) editorRef.current?.editor?.setMergeTags(mergeTags);
  }, [ready, mergeTags]);

  const exportContent = useCallback(
    () => new Promise<UnlayerContent | null>(resolve => {
      const editor = editorRef.current?.editor;
      if (!editor) {
        resolve(null);
        return;
      }
      editor.exportHtml(({ html, design }) => resolve({ html, design }));
    }),
    [],
  );

  const exportAndCache = useCallback(async () => {
    const content = await exportContent();
    if (content) latestContentRef.current = content;
    return content;
  }, [exportContent]);

  const handleReady = useCallback(() => {
    setReady(true);
    const editor = editorRef.current?.editor;
    if (!editor) return;
    const { design, html } = initialRef.current;
    if (design) {
      editor.loadDesign(design as UnlayerDesign);
      latestContentRef.current = { html: html ?? '', design };
    } else {
      exportAndCache();
    }
    editor.addEventListener('design:updated', async () => {
      const content = await exportAndCache();
      if (content) onOfferIndicesChangeRef.current?.(extractOfferIndices(content.html));
    });
  }, [exportAndCache, onOfferIndicesChangeRef]);

  const getCachedContent = useCallback(
    async () => latestContentRef.current ?? exportAndCache(),
    [exportAndCache],
  );

  const insertBlock = useCallback(async (block: InsertedBlock) => {
    const content = await exportContent();
    const editor = editorRef.current?.editor;
    if (!content || !editor) return false;
    editor.loadDesign(appendBlock(content.design as UnlayerDesign, block));
    return true;
  }, [exportContent]);

  return { editorRef, ready, handleReady, exportContent, exportAndCache, getCachedContent, insertBlock };
};

export default useUnlayerEditor;
